import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Film, LogOut, Pencil, Plus, Trash2, Upload, X } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { adminFetch, adminUpload, adminUploadVideo, clearToken, getToken } from "@/admin/api";
import { mediaUrl } from "@/lib/mediaUrl";

const TEXT = {
  ar: {
    title: "فيديوهات SaaS Explainers", subtitle: "ارفع الفيديوهات، عدّل بياناتها، وتحكم بما يظهر في الموقع.",
    resources: "الملحقات", projects: "المشاريع", site: "عرض الموقع", logout: "خروج",
    add: "فيديو جديد", empty: "لا توجد فيديوهات بعد.", loading: "جارٍ التحميل…", retry: "إعادة المحاولة",
    edit: "تعديل الفيديو", create: "إضافة فيديو", titleField: "العنوان", subtitleField: "الوصف القصير",
    video: "الفيديو", poster: "الصورة المصغرة", videoUrl: "رابط الفيديو", posterUrl: "رابط الصورة المصغرة",
    uploadVideo: "رفع MP4 أو WebM", uploadPoster: "رفع صورة", uploading: "جارٍ الرفع…",
    videoHint: "الحد الأقصى 200 ميغابايت. يمكنك أيضًا إدخال رابط مباشر للفيديو.",
    order: "ترتيب العرض", published: "منشور", hidden: "مخفي", show: "إظهار", hide: "إخفاء",
    save: "حفظ", saving: "جارٍ الحفظ…", cancel: "إلغاء", delete: "حذف", saved: "تم حفظ الفيديو.",
    deleted: "تم حذف الفيديو.", confirm: "حذف هذا الفيديو نهائيًا من الموقع؟",
    required: "العنوان بالعربية والإنجليزية مطلوب.",
    tooLarge: "الحد الأقصى للفيديو 200 ميغابايت.", imageTooLarge: "الحد الأقصى للصورة 8 ميغابايت.",
  },
  en: {
    title: "SaaS Explainers", subtitle: "Upload videos, edit details, and choose what appears on the site.",
    resources: "Resources", projects: "Projects", site: "View site", logout: "Log out",
    add: "New video", empty: "No videos yet.", loading: "Loading…", retry: "Retry",
    edit: "Edit video", create: "Add video", titleField: "Title", subtitleField: "Short description",
    video: "Video", poster: "Poster", videoUrl: "Video URL", posterUrl: "Poster URL",
    uploadVideo: "Upload MP4 or WebM", uploadPoster: "Upload image", uploading: "Uploading…",
    videoHint: "200 MB maximum. You can also enter a direct video URL.",
    order: "Display order", published: "Published", hidden: "Hidden", show: "Show", hide: "Hide",
    save: "Save", saving: "Saving…", cancel: "Cancel", delete: "Delete", saved: "Video saved.",
    deleted: "Video deleted.", confirm: "Permanently remove this video from the site?",
    required: "Arabic and English titles are required.",
    tooLarge: "Videos must be 200 MB or smaller.", imageTooLarge: "Images must be 8 MB or smaller.",
  },
  fr: {
    title: "SaaS Explainers", subtitle: "Ajoutez des vidéos, modifiez leurs détails et choisissez celles à afficher.",
    resources: "Ressources", projects: "Projets", site: "Voir le site", logout: "Déconnexion",
    add: "Nouvelle vidéo", empty: "Aucune vidéo pour le moment.", loading: "Chargement…", retry: "Réessayer",
    edit: "Modifier la vidéo", create: "Ajouter une vidéo", titleField: "Titre", subtitleField: "Description courte",
    video: "Vidéo", poster: "Miniature", videoUrl: "URL de la vidéo", posterUrl: "URL de la miniature",
    uploadVideo: "Ajouter MP4 ou WebM", uploadPoster: "Ajouter une image", uploading: "Envoi…",
    videoHint: "200 Mo maximum. Vous pouvez aussi saisir une URL directe.",
    order: "Ordre d'affichage", published: "Publié", hidden: "Masqué", show: "Afficher", hide: "Masquer",
    save: "Enregistrer", saving: "Enregistrement…", cancel: "Annuler", delete: "Supprimer", saved: "Vidéo enregistrée.",
    deleted: "Vidéo supprimée.", confirm: "Supprimer définitivement cette vidéo du site ?",
    required: "Les titres arabe et anglais sont obligatoires.",
    tooLarge: "La vidéo doit faire 200 Mo ou moins.", imageTooLarge: "L'image doit faire 8 Mo ou moins.",
  },
};

const EMPTY = {
  title_ar: "", title_en: "", title_fr: "",
  subtitle_ar: "", subtitle_en: "", subtitle_fr: "",
  video_url: "", poster_url: "", published: false, sort_order: 0,
};
const toForm = (video) => Object.fromEntries(Object.keys(EMPTY).map((key) => [key, video?.[key] ?? EMPTY[key]]));
const inputClass = "mt-1.5 w-full rounded-xl border border-line bg-white px-4 py-2.5 text-sm text-charcoal outline-none focus:border-sage";
const buttonClass = "inline-flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2 text-sm text-graphite transition-colors hover:border-sage hover:text-sage-deep";
const LANGUAGES = [{ key: "ar", label: "العربية" }, { key: "en", label: "English" }, { key: "fr", label: "Français" }];

export default function AdminExplainers() {
  const { lang } = useLanguage();
  const t = TEXT[lang];
  const navigate = useNavigate();
  const [videos, setVideos] = useState(null);
  const [error, setError] = useState("");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editLang, setEditLang] = useState("ar");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await adminFetch("/admin/saas-explainers");
      setVideos(data.videos || []);
      setError("");
    } catch (err) {
      if (err.message !== "Unauthorized") setError(err.message);
    }
  }, []);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login", { replace: true });
      return;
    }
    load();
  }, [load, navigate]);

  const openForm = (video = null) => {
    setForm(toForm(video));
    setEditLang("ar");
    setModal(video ? { mode: "edit", video } : { mode: "create" });
  };

  const save = async (event) => {
    event.preventDefault();
    if (!form.title_ar.trim() || !form.title_en.trim()) {
      toast.error(t.required);
      return;
    }
    setSaving(true);
    try {
      const path = modal.mode === "create" ? "/admin/saas-explainers" : `/admin/saas-explainers/${modal.video.id}`;
      await adminFetch(path, { method: modal.mode === "create" ? "POST" : "PUT", body: JSON.stringify({ ...form, sort_order: Number(form.sort_order) }) });
      toast.success(t.saved);
      setModal(null);
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (video) => {
    try {
      await adminFetch(`/admin/saas-explainers/${video.id}`, {
        method: "PUT", body: JSON.stringify({ ...toForm(video), published: !video.published }),
      });
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const remove = async (video) => {
    if (!window.confirm(t.confirm)) return;
    try {
      await adminFetch(`/admin/saas-explainers/${video.id}`, { method: "DELETE" });
      toast.success(t.deleted);
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const upload = async (event, kind) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const limit = kind === "video" ? 200 : 8;
    if (file.size > limit * 1024 * 1024) {
      toast.error(kind === "video" ? t.tooLarge : t.imageTooLarge);
      event.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const result = await (kind === "video" ? adminUploadVideo(file) : adminUpload(file));
      setForm((current) => ({ ...current, [kind === "video" ? "video_url" : "poster_url"]: result.url }));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  return (
    <div className="min-h-screen bg-softwhite text-charcoal" data-testid="admin-explainers">
      <Toaster position="bottom-center" />
      <header className="border-b border-line bg-white/90">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-5 py-4">
          <Link to="/"><img src={mediaUrl("/brand/logo-primary.png")} alt="YacoubCreates" className="h-6 w-auto" /></Link>
          <nav className="flex flex-wrap items-center gap-2" aria-label={t.title}>
            <Link to="/admin/resources" className={buttonClass}>{t.resources}</Link>
            <Link to="/admin/projects" className={buttonClass}>{t.projects}</Link>
            <Link to="/" className={buttonClass}>{t.site}</Link>
            <LanguageSwitcher />
            <button type="button" className={buttonClass} onClick={() => { clearToken(); navigate("/admin/login"); }}><LogOut size={16} />{t.logout}</button>
          </nav>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-5 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div><h1 className="font-display text-3xl font-bold">{t.title}</h1><p className="mt-2 text-sm text-graphite/60">{t.subtitle}</p></div>
          <button type="button" onClick={() => openForm()} className="inline-flex items-center gap-2 rounded-full bg-charcoal px-6 py-3 text-sm font-semibold text-white"><Plus size={16} />{t.add}</button>
        </div>
        {error && <div role="alert" className="mt-8 flex flex-wrap items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}<button type="button" onClick={load} className="underline">{t.retry}</button></div>}
        <div className="mt-8 grid gap-4">
          {videos === null && !error && <p className="py-12 text-center text-graphite/60">{t.loading}</p>}
          {videos?.length === 0 && !error && <p className="rounded-3xl border border-dashed border-line bg-white py-12 text-center text-graphite/60">{t.empty}</p>}
          {videos?.map((video) => (
            <article key={video.id} className="flex flex-wrap items-center gap-4 rounded-3xl border border-line bg-white p-4 shadow-card">
              {video.poster_url ? <img src={video.poster_url} alt="" className="h-20 w-32 rounded-2xl object-cover" /> : <div className="flex h-20 w-32 items-center justify-center rounded-2xl bg-mist"><Film className="text-sage-deep" /></div>}
              <div className="min-w-0 flex-1">
                <h2 className="font-bold">{video[`title_${lang}`] || video.title_en || video.title_ar}</h2>
                <p className="mt-1 text-xs text-graphite/60">{video[`subtitle_${lang}`] || video.subtitle_en}</p>
                <span className={`mt-2 inline-block rounded-full px-3 py-1 text-xs font-semibold ${video.published ? "bg-sage/15 text-sage-deep" : "bg-graphite/10 text-graphite"}`}>{video.published ? t.published : t.hidden}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" aria-label={video.published ? t.hide : t.show} title={video.published ? t.hide : t.show} className={buttonClass} onClick={() => toggle(video)}>{video.published ? <EyeOff size={16} /> : <Eye size={16} />}<span>{video.published ? t.hide : t.show}</span></button>
                <button type="button" aria-label={t.edit} className={buttonClass} onClick={() => openForm(video)}><Pencil size={16} />{t.edit}</button>
                <button type="button" aria-label={t.delete} className={`${buttonClass} text-red-600 hover:border-red-300 hover:text-red-700`} onClick={() => remove(video)}><Trash2 size={16} />{t.delete}</button>
              </div>
            </article>
          ))}
        </div>
      </main>

      {modal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-charcoal/60 p-3" onMouseDown={(event) => { if (event.target === event.currentTarget && !uploading && !saving) setModal(null); }}>
        <div role="dialog" aria-modal="true" aria-label={modal.mode === "create" ? t.create : t.edit} className="max-h-[94vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-softwhite p-5 shadow-soft md:p-8">
          <div className="flex items-center justify-between"><h2 className="font-display text-2xl font-bold">{modal.mode === "create" ? t.create : t.edit}</h2><button type="button" disabled={uploading || saving} onClick={() => setModal(null)} aria-label={t.cancel}><X size={20} /></button></div>
          <form onSubmit={save} className="mt-6 space-y-6">
            <div className="flex flex-wrap gap-2 border-b border-line pb-3">{LANGUAGES.map(({ key, label }) => <button key={key} type="button" onClick={() => setEditLang(key)} className={`rounded-full px-4 py-2 text-sm ${editLang === key ? "bg-charcoal text-white" : "bg-white text-graphite"}`}>{label}</button>)}</div>
            <div className="grid gap-4 sm:grid-cols-2">
              {["title", "subtitle"].map((field) => <label key={`${field}_${editLang}`} className="block text-sm font-semibold">{field === "title" ? t.titleField : t.subtitleField}{field === "title" && editLang !== "fr" ? " *" : ""}
                <input className={inputClass} dir={editLang === "ar" ? "rtl" : "ltr"} value={form[`${field}_${editLang}`]} onChange={(event) => setForm({ ...form, [`${field}_${editLang}`]: event.target.value })} />
              </label>)}
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div><p className="text-sm font-semibold">{t.video}</p><label className={`${buttonClass} mt-2 cursor-pointer`}><Upload size={16} />{uploading ? t.uploading : t.uploadVideo}<input type="file" accept="video/mp4,video/webm,.mp4,.webm" disabled={uploading} className="hidden" onChange={(event) => upload(event, "video")} /></label><p className="mt-2 text-xs text-graphite/60">{t.videoHint}</p><label className="mt-3 block text-sm font-semibold">{t.videoUrl}<input className={inputClass} dir="ltr" required value={form.video_url} onChange={(event) => setForm({ ...form, video_url: event.target.value })} /></label></div>
              <div><p className="text-sm font-semibold">{t.poster}</p><label className={`${buttonClass} mt-2 cursor-pointer`}><Upload size={16} />{uploading ? t.uploading : t.uploadPoster}<input type="file" accept="image/jpeg,image/png,image/webp" disabled={uploading} className="hidden" onChange={(event) => upload(event, "poster")} /></label><label className="mt-3 block text-sm font-semibold">{t.posterUrl}<input className={inputClass} dir="ltr" value={form.poster_url} onChange={(event) => setForm({ ...form, poster_url: event.target.value })} /></label>{form.poster_url && <img src={form.poster_url} alt="" className="mt-3 h-28 w-full rounded-xl object-cover" />}</div>
            </div>
            {form.video_url && <video key={form.video_url} controls preload="metadata" poster={form.poster_url || undefined} src={form.video_url} className="max-h-72 w-full rounded-xl bg-black" />}
            <div className="flex flex-wrap items-end gap-6"><label className="block text-sm font-semibold">{t.order}<input type="number" min="0" max="10000" className={`${inputClass} w-32`} value={form.sort_order} onChange={(event) => setForm({ ...form, sort_order: event.target.value })} /></label><label className="flex items-center gap-2 pb-3 text-sm font-semibold"><input type="checkbox" checked={form.published} onChange={(event) => setForm({ ...form, published: event.target.checked })} className="size-4 accent-sage" />{t.published}</label></div>
            <div className="flex justify-end gap-3"><button type="button" disabled={saving || uploading} className={buttonClass} onClick={() => setModal(null)}>{t.cancel}</button><button type="submit" disabled={saving || uploading} className="rounded-full bg-charcoal px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-50">{saving ? t.saving : t.save}</button></div>
          </form>
        </div>
      </div>}
    </div>
  );
}
